import React from 'react'
import { ArrowBack, Settings, SportsEsports, MenuBook } from '@mui/icons-material'
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Logo from '../../assets/images/dresez-icon.svg'
import { useSelector } from 'react-redux';
import { getCurrentUserId } from '../../utilities/auth';
import { useFetchIndication } from '../../utilities/chatUtils';

// Top bar. Home is reached via the logo; Games (an outbound link to
// games.dresez.com) / Blog / My Posts / Chats / Settings are the right-side
// icons (the bottom tab bar was retired).
// Sub-screens add the back arrow via AppLayout's `back` prop.
//
// Layout note: Bootstrap's .navbar comes with `flex-wrap: wrap`, which on
// narrow viewports pushed the icon strip onto a second row below the brand
// — the user-visible "icons not right aligned" bug. We pin the row to
// flex-nowrap and let the brand text collapse responsively instead:
//   < 400px: logo only
//   400–639px: logo + "dresez.com"
//   ≥ 640px (sm): logo + "dresez.com" + tagline
// The icon strip is flex-shrink-0 so it never gets squeezed.
export default function Header({ back }) {
  const history = useHistory()
  const intl = useIntl();
  const user = useSelector((state) => state.user?.user);
  // Chat unread indicator — same source the old bottom tab used.
  const firebaseUser = user?.id || getCurrentUserId();
  const { list: chatIndication } = useFetchIndication(firebaseUser);
  const hasUnread = !!chatIndication?.conversationId;
  // Nudge signed-in users with an incomplete profile (profile now lives in Settings).
  const profileIncomplete = !!user?.id && !user?.isProfileCompleted;

  return (
    <div className='w-full'>
      <nav className="py-2 bg-[#f8f9fa] dark:!bg-[#17181c] dark:border-b dark:border-[#2a2c30] flex items-center justify-center">
        <div className="px-3 mx-auto max-w-[1400px] w-full flex items-center justify-between gap-2 flex-nowrap">
          <div className='flex items-center min-w-0'>
            {back && <ArrowBack onClick={() => history.goBack()} className='mr-2 cursor-pointer text-black dark:text-white flex-shrink-0' />}
            <Link to={`/`} className='min-w-0'>
              <div className='flex items-center cursor-pointer min-w-0'>
                <img src={Logo} alt="dresez" className='w-[40px] h-[40px] object-contain rounded-md flex-shrink-0' />
                <div className='ml-2 hidden min-[400px]:block min-w-0'>
                  <h2 className='text-[20px] text-black dark:text-white whitespace-nowrap leading-tight'>dresez.com</h2>
                  <h4 className='text-[14px] text-black dark:text-gray-300 whitespace-nowrap leading-tight hidden sm:block'>
                    <FormattedMessage id="header.tagline" defaultMessage="sell. shop. worldwide." />
                  </h4>
                </div>
              </div>
            </Link>
          </div>
          {/* Right-side icon strip. Order: discovery surfaces first (Games —
              outbound to games.dresez.com, Blog), then personal surfaces
              (MyPosts, Chats, Settings).
              flex-shrink-0 protects the row from being squeezed — the brand
              text block is the only flexible cell. */}
          <div className='flex items-center gap-4 flex-shrink-0'>
            {/* Games left dresez.com — they live on games.dresez.com now. The
                icon keeps its nav slot but points out to that site (new tab,
                noopener so the opened page can't touch window.opener).
                `noreferrer` is deliberately absent — do not add it back as a
                "security fix". Both sites are ours, and this link IS the
                cross-promotion, so the click has to land on games.dresez.com
                as a referral rather than as direct/unknown traffic. Our
                Referrer-Policy is strict-origin-when-cross-origin
                (devops/nginx/conf.d/ssl-params.inc), so the games site sees
                exactly `https://dresez.com/` and never a path — which is
                precisely what we want recorded. noopener, not noreferrer, is
                what protects window.opener. */}
            <a href='https://games.dresez.com/' target='_blank' rel='noopener' aria-label={intl.formatMessage({ id: 'header.aria.games', defaultMessage: 'Games' })} className='relative flex'>
              <SportsEsports className='cursor-pointer text-black dark:text-white' />
            </a>
            <Link to={`/blog`} aria-label={intl.formatMessage({ id: 'header.aria.blog', defaultMessage: 'Blog' })} className='relative flex'>
              <MenuBook className='cursor-pointer text-black dark:text-white' />
            </Link>
            <Link to={`/posts`} aria-label={intl.formatMessage({ id: 'header.aria.myPosts', defaultMessage: 'My posts' })} className='relative flex'>
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' className='w-6 h-6 cursor-pointer text-black dark:text-white'>
                <rect x='3' y='4' width='18' height='4' rx='2' />
                <rect x='3' y='10' width='18' height='4' rx='2' />
                <rect x='3' y='16' width='18' height='4' rx='2' />
              </svg>
            </Link>
            <Link to={`/chats`} aria-label={intl.formatMessage({ id: 'header.aria.chats', defaultMessage: 'Chats' })} className='relative flex'>
              <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' className='w-6 h-6 cursor-pointer text-black dark:text-white'>
                <path d='M5 4h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8l-4 4V4z' />
              </svg>
              {hasUnread && <span className='absolute -top-1 -right-1 w-[8px] h-[8px] rounded-full bg-red-500' />}
            </Link>
            <Link to={`/settings`} aria-label={intl.formatMessage({ id: 'header.aria.settings', defaultMessage: 'Settings' })} className='relative flex'>
              <Settings className='cursor-pointer text-black dark:text-white' />
              {profileIncomplete && <span className='absolute -top-1 -right-1 w-[8px] h-[8px] rounded-full bg-red-500' />}
            </Link>
          </div>
        </div>
      </nav>
    </div>
  )
}
